import { useCallback } from 'react';

import { NETWORK, useBtcWallet } from '@/networks/btc-like/btc';
import { ALLOWED_SIGN_HASH_TYPES, createSendTransaction, ResultUtxo } from '@/networks/btc-like/common';

export const useBtcUtxoMergeTransaction = () => {
  const { address, publicKey, signPsbt } = useBtcWallet();

  const createMergeUtxosTransaction = useCallback(
    async (
      selectedUtxos: ResultUtxo[],
      vbyteEstimate?: number
    ): Promise<Awaited<ReturnType<typeof createSendTransaction>> | null> => {
      if (!address || !publicKey || selectedUtxos.length === 0) {
        return null;
      }

      const params = {
        publicKey,
        recipient: address,
        walletAddress: address,
        vbyteEstimate,
        enforcedUtxoSelection: selectedUtxos,
      };

      const totalSelectedUtxosValue = selectedUtxos.reduce((acc, utxo) => acc + utxo.value, 0);

      const preflightTxData = await createSendTransaction({
        ...params,
        amount: totalSelectedUtxosValue,
        network: NETWORK,
      });
      const { uncoveredValue, mempoolTip } = preflightTxData || {};

      const totalAmount = totalSelectedUtxosValue - Math.abs(Number(uncoveredValue)) - Number(mempoolTip);

      if (totalAmount < 0) {
        return preflightTxData;
      }

      return await createSendTransaction({
        ...params,
        amount: totalAmount,
        network: NETWORK,
      });
    },
    [address]
  );

  const executeMergeUtxosTransaction = useCallback(
    async (txData: Awaited<ReturnType<typeof createMergeUtxosTransaction>>) => {
      if (!address || !txData) {
        throw new Error('It is not possible to execute transaction');
      }

      const { sighTx, psbtB64, uncoveredValue } = txData;

      if (uncoveredValue < 0n) {
        throw new Error(`The selected UTXOs are insufficient to fund this transaction and its fees. ${uncoveredValue}`);
      }

      const options = {
        finalizeSignalInput: false,
        sighTx,
        allowedSignHash: ALLOWED_SIGN_HASH_TYPES.ALL,
        broadcast: true,
      };

      await signPsbt(psbtB64, options);
    },
    [address]
  );

  return {
    createMergeUtxosTransaction,
    executeMergeUtxosTransaction,
  };
};
